import {
    PortfolioItem,
    portfolioItemOrderValues,
} from 'portfolio/PortfolioItem';

import {
    PortfolioSummaryAggregatedData,
    PortfolioSummaryAggregationItem,
} from '../store/data/PortfolioSummaryDataState';
import { Logger } from '@instinet-analytics/core-lib';
import { FilteredPortfolioChangedPayload } from 'portfolio/store/actions';
import { AggregationStrategy } from './strategies/AggregationStrategy';
import { AggregationField } from 'widgets/portfolioSummary/aggregation/fields/aggregationFieldPortfolio';
import { getAggregationStrategy } from 'widgets/portfolioSummary/aggregation/strategies/AggregationStrategyFactory';
import { PortfolioSummaryDrillDown } from 'widgets/portfolioSummary/store/instances/component/PortfolioSummaryComponentState';

interface CountedPortfolioItem extends PortfolioItem {
    count: number;
}

export function computeFullAggregatedData(
    logger: Logger,
    filteredPortfolio: PortfolioItem[],
    aggregationStrategy: AggregationStrategy
): PortfolioSummaryAggregatedData {
    if (filteredPortfolio.length === 0) {
        return {};
    }

    logger.debug(
        'Aggregating filtered portfolio by ' +
            aggregationStrategy.aggregationField
    );

    const aggregatedData: PortfolioSummaryAggregatedData =
        aggregationStrategy.initialiseAggregatedData();

    filteredPortfolio.reduce((data, item) => {
        reduceItem(data, item, aggregationStrategy);
        return data;
    }, aggregatedData);

    logger.debug(
        'Completed aggregating filtered portfolio by ' +
            aggregationStrategy.aggregationField
    );

    return aggregatedData;
}

export function applyChanges(
    changeEvent: FilteredPortfolioChangedPayload,
    prevAggregatedData: PortfolioSummaryAggregatedData,
    aggregationStrategy: AggregationStrategy
): PortfolioSummaryAggregatedData {
    const aggregatedData = { ...prevAggregatedData };

    if (changeEvent.updates?.added) {
        for (const newMember of changeEvent.updates.added) {
            reduceItem(aggregatedData, newMember.next!, aggregationStrategy);
        }
        for (const updatedMember of changeEvent.updates.updated) {
            reduceItem(
                aggregatedData,
                deduct(toCountedPortfolioItem(updatedMember.prev!)),
                aggregationStrategy
            );
            reduceItem(
                aggregatedData,
                updatedMember.next!,
                aggregationStrategy
            );
        }
        for (const removedMember of changeEvent.updates.removed) {
            reduceItem(
                aggregatedData,
                deduct(toCountedPortfolioItem(removedMember.prev!)),
                aggregationStrategy
            );
        }
    }
    return aggregatedData;
}

export function computeAggregatedDataDrilldown(
    logger: Logger,
    filteredPortfolio: PortfolioItem[],
    combineSellsAndShorts: boolean,
    aggregationField: AggregationField,
    drillDown: PortfolioSummaryDrillDown
): PortfolioSummaryAggregatedData {
    if (filteredPortfolio.length === 0) {
        return {};
    }

    const { aggregationField: drillDownField, groupName: drillDownGroupName } =
        drillDown;

    logger.debug(
        `DrillDown on  ${aggregationField}/${drillDownField} for group ${drillDownGroupName}`
    );

    const aggregationStrategy = getAggregationStrategy(
        aggregationField,
        combineSellsAndShorts
    );

    const groupItems = aggregationStrategy.filterPortfolio(
        filteredPortfolio,
        drillDownGroupName
    );

    const drilldownFieldAggregationStrategy = getAggregationStrategy(
        drillDownField,
        combineSellsAndShorts
    );

    const aggregatedData: PortfolioSummaryAggregatedData =
        drilldownFieldAggregationStrategy.initialiseAggregatedData();

    groupItems.reduce((data, item) => {
        reduceItem(data, item, drilldownFieldAggregationStrategy);
        return data;
    }, aggregatedData);

    logger.debug(
        `Completed drillDown on  ${aggregationField}/${drillDownField} for group ${drillDownGroupName}`
    );

    return aggregatedData;
}

function deduct(item: CountedPortfolioItem): CountedPortfolioItem {
    return {
        ...inverse(item),
        count: item.count * -1,
    };
}

function inverse(item: PortfolioItem): PortfolioItem {
    return {
        ...item,
        qty: item.qty * -1,
        qtyTraded: item.qtyTraded * -1,
        qtyRemaining: item.qtyRemaining * -1,
    };
}

function toCountedPortfolioItem(item: PortfolioItem): CountedPortfolioItem {
    return { count: 1, ...item };
}

function reduceItem(
    data: PortfolioSummaryAggregatedData,
    item: PortfolioItem,
    aggregationStrategy: AggregationStrategy
) {
    const countedItem = toCountedPortfolioItem(item);

    const aggregationGroup = aggregationStrategy.getAggregationGroup(
        data,
        item
    );

    incrementSide(aggregationGroup.total, countedItem);

    if (item.side === 'B') {
        incrementSide(aggregationGroup.net, countedItem);
        incrementSide(aggregationGroup.buy, countedItem);
    }

    if (item.side === 'S') {
        incrementSide(aggregationGroup.net, deduct(countedItem));
        incrementSide(aggregationGroup.sell, countedItem);
    }

    if (item.side === 'SS') {
        incrementSide(aggregationGroup.net, deduct(countedItem));
        incrementSide(
            aggregationStrategy.combineSellsAndShorts
                ? aggregationGroup.sell
                : aggregationGroup.short!,
            countedItem
        );
    }

    const drillDowns = aggregationStrategy.getDrillDowns(data, item);

    if (drillDowns) {
        for (const drilldownField of Object.keys(drillDowns)) {
            const drilldownAggregationStrategy = getAggregationStrategy(
                drilldownField as AggregationField,
                aggregationStrategy.combineSellsAndShorts
            );
            const aggregatedData = drillDowns[drilldownField];
            reduceItem(aggregatedData, item, drilldownAggregationStrategy);
        }
    }
}

function incrementSide(
    sideValues: PortfolioSummaryAggregationItem,
    item: CountedPortfolioItem
) {
    const { tradedValue, remainingValue, orderedValue } =
        portfolioItemOrderValues(item);

    sideValues.count += item.count;
    sideValues.orderQty += item.qty;
    sideValues.tradedQty += item.qtyTraded;
    sideValues.leavesQty += item.qtyRemaining;
    sideValues.tradedValue += tradedValue;
    sideValues.leavesValue += remainingValue;
    sideValues.orderedValue += orderedValue;
}
